import ButtonGroup from "@legacy/core/buttons/ButtonGroup"
import ButtonGroupRow from "@legacy/core/buttons/ButtonGroupRow"
import UniversalButton from "@legacy/core/buttons/UniversalButton"
import ListSelectField from "@legacy/core/fields/ListSelectField"
import { fetchPriceBookClientOverrides } from "@legacy/pricebook/utils/utils"
import { Component, Fragment } from "react"
import deepcopy from "rfdc"

import { PriceBookItemTypes } from "@constants/choices"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"
import InlineGeneralOverrideContainer from "../InlineGeneralOverrideContainer"
import PriceOverrideListEditSelect from "../components/PriceOverrideListEditSelect"

const GENERAL_OVERRIDE_TYPES = [
    { type: PriceBookItemTypes.service, label: "Service Charges", addLabel: "Add Service Charge General Override" },
    { type: PriceBookItemTypes.part, label: "Parts or Materials", addLabel: "Add Part or Material General Override" },
    { type: PriceBookItemTypes.other, label: "Misc. Charges", addLabel: "Add Misc. Charge General Override" },
]

class PriceBookClientOverridesForm extends Component {
    constructor(props) {
        super(props)

        this.addToastToQueue = this.props.addToastToQueue

        this.state = {
            priceBookOverridesData: null,
            generalOverridesData: null,
            errors: {
                priceBookOverridesDataErrors: null,
            },
        }
    }

    componentDidMount = async () => {
        try {
            const { itemOverrides, generalOverrides } = await fetchPriceBookClientOverrides(this.props.client.id)

            const overridesOfType = (pricebookItemType) =>
                itemOverrides.filter((override) => override.pricebook_item.pricebook_item_type === pricebookItemType)

            this.setState({
                priceBookOverridesData: {
                    service_charges: overridesOfType(PriceBookItemTypes.service),
                    parts: overridesOfType(PriceBookItemTypes.part),
                    other_charges: overridesOfType(PriceBookItemTypes.other),
                    discounts: overridesOfType(PriceBookItemTypes.discount),
                },
                generalOverridesData: Object.fromEntries(
                    generalOverrides.map((override) => [override.pricebook_item_type, override]),
                ),
            })
        } catch (error) {
            this.setState((state) => ({ errors: { ...state.errors, priceBookOverridesDataErrors: error } }))
        }
    }

    updateOverride = (type, index, updatedItem) => {
        this.setState((state) => {
            const updatedpriceBookOverridesData = { ...state.priceBookOverridesData }
            updatedpriceBookOverridesData[type][index] = updatedItem

            return { priceBookOverridesData: updatedpriceBookOverridesData }
        })
    }

    deleteOverride = (type, item) => {
        this.setState((state) => {
            const updatedpriceBookOverridesData = { ...state.priceBookOverridesData }
            updatedpriceBookOverridesData[type] = updatedpriceBookOverridesData[type].filter(
                (data) => data.pricebook_item.id !== item.pricebook_item.id,
            )

            return { priceBookOverridesData: updatedpriceBookOverridesData }
        })
    }

    addGeneralOverride = (pricebookItemType) => {
        this.setState((state) => ({
            generalOverridesData: {
                ...state.generalOverridesData,
                [pricebookItemType]: { __new__: true, pricebook_item_type: pricebookItemType },
            },
        }))
    }

    updateGeneralOverride = (pricebookItemType, updatedItem) => {
        this.setState((state) => ({
            generalOverridesData: { ...state.generalOverridesData, [pricebookItemType]: updatedItem },
        }))
    }

    deleteGeneralOverride = (pricebookItemType) => {
        this.setState((state) => {
            const generalOverridesData = { ...state.generalOverridesData }
            delete generalOverridesData[pricebookItemType]

            return { generalOverridesData }
        })
    }

    renderGeneralOverride = ({ type, label, addLabel }) => {
        const generalOverride = this.state.generalOverridesData[type]

        return (
            <ListSelectField
                key={type}
                fieldName={`general_override_${type}`}
                fieldLabel={label}
                inputComponent={
                    <div id={`id_general_override_${type}`} className="data-panel__list-select">
                        {generalOverride && (
                            <InlineGeneralOverrideContainer
                                key={`general_override_${type}_${generalOverride.id ?? "new"}`}
                                pricebookItemType={type}
                                generalOverride={generalOverride}
                                updateOverride={(updatedItem) => this.updateGeneralOverride(type, updatedItem)}
                                deleteOverride={() => this.deleteGeneralOverride(type)}
                                addToastToQueue={this.addToastToQueue}
                                client={this.props.client}
                            />
                        )}
                    </div>
                }
                showButton={!generalOverride && window.CURRENT_USER?.permissions.pricebook_edit_permission >= PERMISSION_LEVEL.FULL}
                buttonLabel={addLabel}
                buttonAction={(event) => this.addGeneralOverride(type)}
                errors={this.state.errors}
            ></ListSelectField>
        )
    }

    render = () => {
        const { client, formatCurrencyValue, nonFieldErrorMessage } = this.props

        const newServiceChargeExistsInList = (this.state.priceBookOverridesData?.service_charges || []).some(
            (item) => item.__new__ || false,
        )
        const newPartExistsInList = (this.state.priceBookOverridesData?.parts || []).some(
            (item) => item.__new__ || false,
        )
        const newOtherExistsInList = (this.state.priceBookOverridesData?.other_charges || []).some(
            (item) => item.__new__ || false,
        )
        const newDiscountExistsInList = (this.state.priceBookOverridesData?.discounts || []).some(
            (item) => item.__new__ || false,
        )

        return (
            <div className="data-panel-container data-panel-container--with-margin">
                <div className="data-panel" aria-label="Client Deactivate">
                    <div className="data-panel__form" aria-label="Client Deactivate Form">
                        <Fragment>
                            <div
                                className="data-panel__form__field"
                                id="div_id_line_items"
                                aria-label="Line Item Errors"
                                style={
                                    this.state.errors.priceBookOverridesDataErrors
                                        ? { display: "initial" }
                                        : { display: "none" }
                                }
                            >
                                {this.state.errors.priceBookOverridesDataErrors && (
                                    <div
                                        className="data-panel__form__field__errors text-centered"
                                        aria-label="Field Errors"
                                    >
                                        {nonFieldErrorMessage}
                                    </div>
                                )}
                            </div>
                            <p className="data-panel__form__caption"><strong>General Overrides</strong></p>
                            <p className="data-panel__form__caption text-optional">Discount every item of a type for this client. Individual item overrides take priority.</p>
                            {this.state.generalOverridesData !== null && GENERAL_OVERRIDE_TYPES.map(this.renderGeneralOverride)}

                            <p className="data-panel__form__caption"><strong>Individual Overrides</strong></p>

                            <ListSelectField
                                fieldName="service_charges"
                                fieldLabel="Service Charge Overrides"
                                inputComponent={
                                    <PriceOverrideListEditSelect
                                        fieldName="service"
                                        formatCurrencyValue={formatCurrencyValue}
                                        currencySymbol={this.props.currencySymbol}
                                        priceBookOverridesData={
                                            this.state.priceBookOverridesData?.service_charges || []
                                        }
                                        updateOverride={(index, item) =>
                                            this.updateOverride("service_charges", index, item)
                                        }
                                        deleteOverride={(item) => this.deleteOverride("service_charges", item)}
                                        addToastToQueue={this.addToastToQueue}
                                        client={this.props.client}
                                    />
                                }
                                showButton={!newServiceChargeExistsInList && window.CURRENT_USER?.permissions.pricebook_edit_permission >= PERMISSION_LEVEL.FULL}
                                buttonLabel="Add Service Charge Override"
                                buttonAction={(event) => {
                                    let newOverride = deepcopy()(
                                        this.state.priceBookOverridesData?.service_charges || [],
                                    )
                                    newOverride.push({
                                        external_client: client.id,
                                        pricebook_item: {
                                            pricebook_item_type: PriceBookItemTypes.service,
                                        },
                                        price: "",
                                        __new__: true,
                                    })

                                    this.setState((state, props) => {
                                        let updatedState = state
                                        updatedState.priceBookOverridesData.service_charges = newOverride
                                        return updatedState
                                    })
                                }}
                                errors={this.state.errors}
                            ></ListSelectField>
                            <ListSelectField
                                fieldName="parts"
                                fieldLabel="Part or Material Overrides"
                                inputComponent={
                                    <PriceOverrideListEditSelect
                                        fieldName="part"
                                        formatCurrencyValue={formatCurrencyValue}
                                        currencySymbol={this.props.currencySymbol}
                                        priceBookOverridesData={this.state.priceBookOverridesData?.parts || []}
                                        updateOverride={(index, item) => this.updateOverride("parts", index, item)}
                                        deleteOverride={(item) => this.deleteOverride("parts", item)}
                                        addToastToQueue={this.addToastToQueue}
                                        client={this.props.client}
                                    />
                                }
                                showButton={!newPartExistsInList && window.CURRENT_USER?.permissions.pricebook_edit_permission >= PERMISSION_LEVEL.FULL}
                                buttonLabel="Add Part or Material Override"
                                buttonAction={(event) => {
                                    let newOverride = deepcopy()(this.state.priceBookOverridesData?.parts || [])
                                    newOverride.push({
                                        external_client: client.id,
                                        pricebook_item: {
                                            pricebook_item_type: PriceBookItemTypes.part,
                                        },
                                        price: "",
                                        __new__: true,
                                    })

                                    this.setState((state, props) => {
                                        let updatedState = state
                                        updatedState.priceBookOverridesData.parts = newOverride
                                        return updatedState
                                    })
                                }}
                                errors={this.state.errors}
                            ></ListSelectField>
                            <ListSelectField
                                fieldName="other_charges"
                                fieldLabel="Misc. Charge Overrides"
                                inputComponent={
                                    <PriceOverrideListEditSelect
                                        fieldName="other"
                                        formatCurrencyValue={formatCurrencyValue}
                                        currencySymbol={this.props.currencySymbol}
                                        priceBookOverridesData={this.state.priceBookOverridesData?.other_charges || []}
                                        updateOverride={(index, item) =>
                                            this.updateOverride("other_charges", index, item)
                                        }
                                        deleteOverride={(item) => this.deleteOverride("other_charges", item)}
                                        addToastToQueue={this.addToastToQueue}
                                        client={this.props.client}
                                    />
                                }
                                showButton={!newOtherExistsInList && window.CURRENT_USER?.permissions.pricebook_edit_permission >= PERMISSION_LEVEL.FULL}
                                buttonLabel="Add Misc. Charge Override"
                                buttonAction={(event) => {
                                    let newOverride = deepcopy()(
                                        this.state.priceBookOverridesData?.other_charges || [],
                                    )
                                    newOverride.push({
                                        external_client: client.id,
                                        pricebook_item: {
                                            pricebook_item_type: PriceBookItemTypes.other,
                                        },
                                        price: "",
                                        __new__: true,
                                    })

                                    this.setState((state, props) => {
                                        let updatedState = state
                                        updatedState.priceBookOverridesData.other_charges = newOverride
                                        return updatedState
                                    })
                                }}
                                errors={this.state.errors}
                            ></ListSelectField>
                            <ListSelectField
                                fieldName="discounts"
                                fieldLabel="Discount Overrides"
                                inputComponent={
                                    <PriceOverrideListEditSelect
                                        fieldName="discount"
                                        formatCurrencyValue={formatCurrencyValue}
                                        currencySymbol={this.props.currencySymbol}
                                        priceBookOverridesData={this.state.priceBookOverridesData?.discounts || []}
                                        updateOverride={(index, item) => this.updateOverride("discounts", index, item)}
                                        deleteOverride={(item) => this.deleteOverride("discounts", item)}
                                        addToastToQueue={this.addToastToQueue}
                                        client={this.props.client}
                                    />
                                }
                                showButton={!newDiscountExistsInList && window.CURRENT_USER?.permissions.pricebook_edit_permission >= PERMISSION_LEVEL.FULL}
                                buttonLabel="Add Discount Override"
                                buttonAction={(event) => {
                                    let newOverride = deepcopy()(this.state.priceBookOverridesData?.discounts || [])
                                    newOverride.push({
                                        external_client: client.id,
                                        pricebook_item: {
                                            pricebook_item_type: PriceBookItemTypes.discount,
                                        },
                                        price: "",
                                        __new__: true,
                                    })

                                    this.setState((state, props) => {
                                        let updatedState = state
                                        updatedState.priceBookOverridesData.discounts = newOverride
                                        return updatedState
                                    })
                                }}
                                errors={this.state.errors}
                            ></ListSelectField>
                        </Fragment>
                    </div>
                    <ButtonGroup>
                        <ButtonGroupRow>
                            <UniversalButton
                                type="secondary"
                                text="Return to Client"
                                handler={(event) => this.props.switchToPrimaryForm()}
                            />
                        </ButtonGroupRow>
                    </ButtonGroup>
                </div>
            </div>
        )
    }
}

export default PriceBookClientOverridesForm
